import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import {
    Alert,
    Button,
    FileInput,
    Paper,
    rem,
    Stack,
    Switch,
    Text,
    Title,
    useMantineColorScheme,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import React, { useState } from 'react';
import { useImportEmployees } from '../hooks/useImportEmployees';
import { ImportHistoryPanel } from './ImportHistoryPanel';
import { ImportResultSummary } from './ImportResultSummary';

const toImportDateParam = (date: Date | null): string | undefined => {
    if (!date) {
        return undefined;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
};

export const EmployeeImportUpload: React.FC = () => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const mutation = useImportEmployees();
    const [file, setFile] = useState<File | null>(null);
    const [autoCreateDictionaries, setAutoCreateDictionaries] = useState(true);
    const [importMonth, setImportMonth] = useState<Date | null>(null);

    const handleFileChange = (value: File | null) => {
        setFile(value);
        mutation.reset();
    };

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAutoCreateDictionaries(event.currentTarget.checked);
    };

    const handleSubmit = () => {
        if (!file) {
            return;
        }
        mutation.mutate({ file, autoCreateDictionaries, importDate: toImportDateParam(importMonth) });
    };

    return (
        <Paper p="md" withBorder bg={isDark ? 'dark.7' : 'gray.0'}>
            <Stack gap="md">
                <Stack gap="xs">
                    <Title order={3} size="h4" fw={600}>
                        Import Employees from File
                    </Title>
                    <Text size="sm" c="dimmed">
                        Upload an Excel (.xlsx, .xls, .ods) or CSV file exported from Enova ERP.
                    </Text>
                </Stack>

                {mutation.isError && (
                    <Alert color="red" title="Import Failed">
                        {mutation.error?.message ?? 'An unexpected error occurred. Please try again.'}
                    </Alert>
                )}

                <FileInput
                    label="Import file"
                    aria-label="Employee import file"
                    accept=".xlsx,.xls,.ods,.csv"
                    placeholder="Choose file..."
                    value={file}
                    onChange={handleFileChange}
                    clearable
                />

                <Switch
                    label="Auto-create dictionary entries"
                    aria-label="Auto-create dictionary entries"
                    description="Automatically create new entries (cost centers, projects, schedules) found in the file"
                    checked={autoCreateDictionaries}
                    onChange={handleToggleChange}
                />

                <MonthPickerInput
                    label="Import month (optional)"
                    aria-label="Import month override"
                    description="Leave blank to use the current month. Set for historical backfill."
                    placeholder="Current month"
                    value={importMonth}
                    onChange={setImportMonth}
                    clearable
                    maxDate={new Date()}
                />

                <Button
                    aria-label="Import Employees"
                    color="brand.5"
                    leftSection={<ArrowUpTrayIcon style={{ width: rem(16), height: rem(16) }} />}
                    disabled={!file || mutation.isPending}
                    loading={mutation.isPending}
                    onClick={handleSubmit}
                    style={{ alignSelf: 'flex-start' }}
                >
                    Import Employees
                </Button>

                {mutation.isSuccess && mutation.data && (
                    <ImportResultSummary result={mutation.data} />
                )}

                <ImportHistoryPanel />
            </Stack>
        </Paper>
    );
};
